/**
 * Production Order Detail Page
 *
 * Displays detailed production order information including:
 * - Status timeline at the top
 * - Order info and BOM reference
 * - Material requirements and tracking
 * - Action buttons for status transitions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  Edit,
  Loader2,
  Factory,
  Package,
  Play,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Truck,
  Calculator,
  Activity,
  History,
  RefreshCw,
  Clock,
} from 'lucide-react';
import Layout from '../components/Layout';
import authService from '../services/authService';
import productionService from '../services/productionService';
import materialRequisitionService from '../services/materialRequisitionService';
import CompleteWorkOrderDialog from '../components/workOrder/CompleteWorkOrderDialog';
import MaterialIssueDialog from '../components/workOrder/MaterialIssueDialog';
import ProductionStepTracker from '../components/production/ProductionStepTracker';

function ProductionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null); // 'warning' | 'destructive' | null
  const [order, setOrder] = useState(null);
  const [userNames, setUserNames] = useState({}); // Map of user_id to display name

  // Dialog states
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [materialRequisitions, setMaterialRequisitions] = useState([]);

  // Load user info
  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
  }, []);

  // Load order data
  const loadOrder = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setErrorType(null);

      const orderData = await productionService.getProductionOrder(id);
      setOrder(orderData);

      // Load user names for progress history
      const progressHistory = orderData.progress_history || [];
      if (progressHistory.length > 0) {
        // Collect unique user IDs from progress_history
        const userIds = [...new Set(progressHistory.map(entry => entry.completed_by).filter(Boolean))];
        if (userIds.length > 0) {
          try {
            const usersMap = await authService.getUsersByIds(userIds);
            setUserNames(usersMap);
          } catch (err) {
            console.error('Failed to load user names:', err);
          }
        }
      }

      // Load material requisitions for this production order
      try {
        const reqResult = await materialRequisitionService.getRequisitionsByProductionOrder(id);
        setMaterialRequisitions(reqResult.requisitions || reqResult.items || []);
      } catch (err) {
        console.error('Failed to load material requisitions:', err);
        setMaterialRequisitions([]);
      }

    } catch (err) {
      console.error('Failed to load production order:', err);
      setError('Failed to load production order details. Please try again.');
      setErrorType('destructive');
    } finally {
      setLoading(false);
      }
    }, [id]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const handleBack = () => {
    navigate('/production');
  };

  const handleEdit = () => {
    navigate(`/production/${id}/edit`);
  };

  const handleRelease = async () => {
    try {
      setActionLoading(true);
      await productionService.releaseOrder(id);
      await loadOrder();
    } catch (err) {
      setError(`Failed to release order: ${err.message}`);
      setErrorType('destructive');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartProduction = async () => {
    try {
      setActionLoading(true);
      await productionService.startProduction(id);
      await loadOrder();
    } catch (err) {
      setError(`Failed to start production: ${err.message}`);
      setErrorType('destructive');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteSuccess = useCallback(async () => {
    await loadOrder();
  }, [loadOrder]);

  const handleCancel = async () => {
    if (!window.confirm('确定要取消此生产工单吗？')) {
      return;
    }

    try {
      setActionLoading(true);
      await productionService.cancelOrder(id);
      await loadOrder();
    } catch (err) {
      setError(`Failed to cancel order: ${err.message}`);
      setErrorType('destructive');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefreshProcessSteps = async () => {
    if (!window.confirm('确定要刷新工序步骤吗？这将从当前BOM模板重新生成工序步骤。')) {
      return;
    }

    try {
      setActionLoading(true);
      await productionService.refreshProcessSteps(id);
      await loadOrder();
      setError('工序步骤已刷新', 'default');
    } catch (err) {
      setError(`Failed to refresh process steps: ${err.message}`);
      setErrorType('destructive');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { variant: 'secondary', label: '草稿' },
      released: { variant: 'default', label: '已发布' },
      in_progress: { variant: 'warning', label: '生产中' },
      completed: { variant: 'success', label: '已完成' },
      cancelled: { variant: 'destructive', label: '已取消' },
    };
    const { variant, label } = config[status] || { variant: 'secondary', label: status };
    return <Badge variant={variant}>{label}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { variant: 'outline', label: '低' },
      normal: { variant: 'secondary', label: '普通' },
      high: { variant: 'default', label: '高' },
      urgent: { variant: 'destructive', label: '紧急' },
    };
    const { variant, label } = config[priority] || { variant: 'secondary', label: priority };
    return <Badge variant={variant}>{label}</Badge>;
  };

  // Status timeline steps
  const statusSteps = [
    { key: 'draft', label: '草稿', icon: FileText },
    { key: 'released', label: '已发布', icon: Play },
    { key: 'in_progress', label: '生产中', icon: Factory },
    { key: 'completed', label: '已完成', icon: CheckCircle },
  ];

  const getCurrentStepIndex = () => {
    if (order?.status === 'cancelled') return -1;
    return statusSteps.findIndex((s) => s.key === order?.status);
  };

  // Check permissions
  const canManage = user && ['admin', 'warehouse_manager', 'engineer'].includes(user.role);

  if (loading && !order) {
    return (
      <Layout>
        <div className="container mx-auto py-6">
          <div className="flex justify-center items-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </Layout>
    );
  }

  if (error && !order) {
    return (
      <Layout>
        <div className="container mx-auto py-6">
          <Alert variant={errorType || 'destructive'}>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button onClick={handleBack} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            返回生产工单
          </Button>
        </div>
      </Layout>
    );
  }

  // Calculate completed quantity from process_steps for consistency
  const calculateCompletedFromSteps = () => {
    if (!order?.items || order.items.length === 0) {
      return Number(order?.completed_quantity || 0);
    }
    
    let totalCompleted = 0;
    order.items.forEach(item => {
      if (item.process_steps && item.process_steps.length > 0) {
        // For multi-item orders, sum up the completed quantity from each item's last completed step
        const lastCompletedStep = [...item.process_steps].reverse().find(step => step.status === 'completed');
        if (lastCompletedStep) {
          totalCompleted += Number(lastCompletedStep.quantity_completed || 0);
        }
      }
    });
    return totalCompleted;
  };

  // Handle both single and multi-item structures for progress calculation
  const plannedQty = Number(order?.total_planned_quantity || order?.planned_quantity || 0);
  const completedQty = calculateCompletedFromSteps();
  const progressPercent = plannedQty > 0 ? (completedQty / plannedQty) * 100 : 0;
  const currentStepIndex = getCurrentStepIndex();

  // Helper function to format MongoDB date
  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    if (typeof dateValue === 'string') return new Date(dateValue).toLocaleString();
    if (dateValue.$date) return new Date(dateValue.$date).toLocaleString();
    return new Date(dateValue).toLocaleString();
  };

  return (
    <Layout>
      <div className="container mx-auto py-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold">{order?.production_order_id}</h1>
                {order && getStatusBadge(order.status)}
                {order && getPriorityBadge(order.priority)}
              </div>
              <p className="text-muted-foreground">
                {order?.finished_product_name} ({order?.finished_product_code})
              </p>
            </div>
          </div>
          {canManage && order?.status === 'draft' && (
            <Button variant="outline" onClick={handleEdit}>
              <Edit className="h-4 w-4 mr-2" />
              编辑
            </Button>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant={errorType || 'destructive'}>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="whitespace-pre-wrap">{error}</AlertDescription>
          </Alert>
        )}

        {/* Status Timeline */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              {statusSteps.map((step, index) => {
                const Icon = step.icon;
                const isActive = currentStepIndex >= index;
                const isCurrent = currentStepIndex === index;

                return (
                  <React.Fragment key={step.key}>
                    <div className="flex flex-col items-center">
                      <div
                        className={`
                          w-12 h-12 rounded-full flex items-center justify-center
                          ${
                            isActive
                              ? isCurrent
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-primary/20 text-primary'
                              : 'bg-muted text-muted-foreground'
                          }
                        `}
                      >
                        <Icon className="h-6 w-6" />
                      </div>
                      <span
                        className={`mt-2 text-sm ${isCurrent ? 'font-medium' : 'text-muted-foreground'}`}
                      >
                        {step.label}
                      </span>
                    </div>
                    {index < statusSteps.length - 1 && (
                      <div
                        className={`flex-1 h-1 mx-4 ${
                          currentStepIndex > index ? 'bg-primary' : 'bg-muted'
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {order?.status === 'cancelled' && (
              <div className="mt-4 p-4 bg-destructive/10 rounded-lg text-center">
                <XCircle className="h-6 w-6 text-destructive mx-auto mb-2" />
                <span className="text-destructive font-medium">工单已取消</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        {canManage && !['completed', 'cancelled'].includes(order?.status) && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex gap-3">
                {order?.status === 'draft' && (
                  <Button onClick={handleRelease} disabled={actionLoading}>
                    <Play className="h-4 w-4 mr-2" />
                    发布生产
                  </Button>
                )}
                {order?.status === 'released' && (
                  <>
                    <Button onClick={() => setIssueDialogOpen(true)} disabled={actionLoading}>
                      <Truck className="h-4 w-4 mr-2" />
                      领料
                    </Button>
                    <Button onClick={handleStartProduction} disabled={actionLoading}>
                      <Factory className="h-4 w-4 mr-2" />
                      开始生产
                    </Button>
                  </>
                )}
                {order?.status === 'in_progress' && (
                  <>
                    <Button onClick={() => setIssueDialogOpen(true)} variant="outline" disabled={actionLoading}>
                      <Truck className="h-4 w-4 mr-2" />
                      追加领料
                    </Button>
                    <Button onClick={() => setCompleteDialogOpen(true)} disabled={actionLoading}>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      完成生产
                    </Button>
                  </>
                )}
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={actionLoading}
                  className="text-destructive hover:text-destructive"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  取消工单
                </Button>
                {user?.role === 'admin' && (
                  <Button
                    variant="ghost"
                    onClick={handleRefreshProcessSteps}
                    disabled={actionLoading}
                    className="ml-auto"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    刷新工序步骤
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Order Details and Progress */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Order Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Factory className="h-5 w-5" />
                工单信息
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">BOM引用</p>
                  <p className="font-mono">{order?.bom_id || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">工作中心</p>
                  <p>{order?.work_center_name || '未分配'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">来源</p>
                  <p>{order?.source_reference || '手动创建'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">客户</p>
                  <p>{order?.client_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">计划开始</p>
                  <p>{order?.planned_start_date || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">计划完成</p>
                  <p>{order?.planned_end_date || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Production Progress */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                生产进度
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>进度</span>
                  <span>
                    {order?.total_completed_quantity || order?.completed_quantity || 0} / {order?.total_planned_quantity || order?.planned_quantity || 0} (
                    {progressPercent.toFixed(1)}%)
                  </span>
                </div>
                <Progress value={progressPercent} />
              </div>

              <div className="grid grid-cols-3 gap-4 pt-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary">{order?.total_planned_quantity || order?.planned_quantity || 0}</p>
                  <p className="text-sm text-muted-foreground">计划</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {completedQty}
                  </p>
                  <p className="text-sm text-muted-foreground">完成</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-destructive">
                    {order?.total_scrapped_quantity || order?.scrap_quantity || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">报废</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>



        {/* Progress History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              生产进度历史
            </CardTitle>
          </CardHeader>
          <CardContent>
            {order?.progress_history?.length > 0 ? (
              <div className="space-y-8 relative pl-2">
                {/* Vertical Line */}
                <div className="absolute left-[19px] top-2 bottom-2 w-0.5 bg-muted-foreground/20" />
                
                {order.progress_history.map((entry, index) => (
                  <div key={index} className="relative flex items-start group">
                    {/* Dot */}
                    <div className="absolute left-[13px] mt-1.5 w-3 h-3 rounded-full border-2 border-primary bg-background group-hover:bg-primary transition-colors z-10" />

                    <div className="ml-10 w-full">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline mb-2">
                        <span className="font-medium text-sm flex items-center gap-2 flex-wrap">
                          {/* Priority: operator_name > userNames map > completed_by > unknown */}
                          <span className="text-foreground">
                            {entry.operator_name ||
                              (entry.completed_by && userNames[entry.completed_by]?.displayName) ||
                              entry.completed_by ||
                              '未知操作员'}
                          </span>
                          <Badge variant="outline" className="text-xs font-normal bg-primary/5">
                            {/* Show product code if available, infer operation type */}
                            {entry.item_product_code && `${entry.item_product_code} `}
                            {entry.operation ||
                              (entry.quantity_scrapped > 0 ? '报废' :
                               entry.quantity_completed > 0 ? '完成' :
                               (entry.step_name || '更新进度'))}
                            {entry.quantity_scrapped > 0 ? ` ${entry.quantity_scrapped}件` :
                             entry.quantity_completed > 0 ? ` ${entry.quantity_completed}件` : ''}
                          </Badge>
                        </span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          {entry.date ? (
                            <>
                              <Clock className="h-3 w-3" />
                              {formatDate(entry.date).split(',')[0]} {formatDate(entry.date).split(',')[1] || ''}
                            </>
                          ) : (
                            <span className="italic">历史记录</span>
                          )}
                        </span>
                      </div>

                      <div className="bg-muted/30 rounded-lg p-3 text-sm border border-border/50">
                        {/* 显示操作详情 */}
                        <div className="flex justify-between items-center flex-wrap gap-2">
                          <span className="text-muted-foreground">
                            {entry.step_sequence ? `工序 ${entry.step_sequence}` : ''}
                          </span>
                          <div className="flex gap-2 flex-wrap">
                            {entry.quantity_completed > 0 && (
                              <Badge variant="secondary" className="bg-green-100 text-green-800 hover:bg-green-100 border-green-200">
                                完成: {entry.quantity_completed} 件
                              </Badge>
                            )}
                            {entry.quantity_scrapped > 0 && (
                              <Badge variant="destructive" className="bg-red-100 text-red-800 hover:bg-red-100 border-red-200">
                                报废: {entry.quantity_scrapped} 件
                              </Badge>
                            )}
                          </div>
                        </div>
                        {entry.notes && (
                          <div className="mt-2 text-xs text-muted-foreground border-t border-border/50 pt-2 flex items-start gap-1">
                            <FileText className="h-3 w-3 mt-0.5" />
                            {entry.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <History className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>暂无生产进度记录</p>
                <p className="text-sm mt-1">工人完成工序步骤后将在此显示</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bar Stock Specific Info */}
        {(() => {
          const barInfos = order?.items
            ? order.items.filter(item => item.bar_info).map(item => ({
                ...item.bar_info,
                product_code: item.finished_product_code,
                product_name: item.finished_product_name
              }))
            : (order?.bar_info ? [order.bar_info] : []);

          if (barInfos.length === 0) return null;

          return (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  棒料物料信息
                </CardTitle>
              </CardHeader>
              <CardContent>
                {barInfos.map((barInfo, idx) => (
                  <div key={idx} className="mb-4 pb-4 border-b last:border-0 last:mb-0 last:pb-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline">
                        {barInfo.product_code} - {barInfo.product_name}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">棒料代码</p>
                        <p className="font-medium">{barInfo.bar_product_code || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">棒料长度</p>
                        <p className="font-medium">{barInfo.bar_length || 310}mm</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">产品长度</p>
                        <p className="font-medium">{barInfo.product_length || '-'}mm</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">每根产出</p>
                        <p className="font-medium">{barInfo.pieces_per_bar || '-'}个</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">需领料</p>
                        <p className="font-medium">{barInfo.ideal_bars || 0} 根</p>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Material Issue Records */}
                {order?.materials_issued?.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium mb-3">领料记录</h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>物料</TableHead>
                          <TableHead className="text-right">数量</TableHead>
                          <TableHead>仓库</TableHead>
                          <TableHead>时间</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {order.materials_issued.map((issue, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-mono">{issue.material_product_id}</TableCell>
                            <TableCell className="text-right">{issue.bar_count}</TableCell>
                            <TableCell>{issue.warehouse_location || '-'}</TableCell>
                            <TableCell>
                              {issue.issued_at ? new Date(issue.issued_at).toLocaleString() : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Remaining Bar Length (if completed) */}
                {order?.status === 'completed' && order?.remaining_bar_length !== null && (
                  <div className="mt-6 p-4 bg-muted rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">剩余棒料长度</p>
                        <p className="text-xl font-bold">
                          {order.remaining_bar_length}mm
                        </p>
                      </div>
                      {order.remaining_bar_length >= 30 && (
                        <Badge variant="success">已创建余料入库</Badge>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()}


        {/* Material Requisitions - Always show when requisitions exist */}
        {materialRequisitions && materialRequisitions.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                领料申请记录
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>申请单号</TableHead>
                    <TableHead>物料列表</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>申请人</TableHead>
                    <TableHead>创建时间</TableHead>
                    <TableHead>操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {materialRequisitions.map((req, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-mono">{req.requisition_id || req.requisition_number || '-'}</TableCell>

                      <TableCell>
                        <div className="max-w-xs truncate">
                          {req.products && req.products.map((p, i) => (
                            <span key={i} className="text-sm">
                              {p.product_name || p.product_code}{i < req.products.length - 1 ? '' : ''}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={req.status === 'approved' ? 'success' : req.status === 'rejected' ? 'destructive' : req.status === 'pending_approval' ? 'warning' : 'secondary'}>
                          {req.status === 'approved' ? '已审批' : req.status === 'rejected' ? '已拒绝' : req.status === 'pending_approval' ? '待审批' : req.status === 'cancelled' ? '已取消' : req.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{req.applicant_name || '-'}</TableCell>
                      <TableCell>{req.created_at ? new Date(req.created_at).toLocaleString() : '-'}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/material-requisitions/${req._id}`)}>
                          查看详情
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
        {/* Process Steps - Show all items, with placeholder if no steps */}
        {order?.items?.length > 0 && (
          <>
            {order.items.map((item, itemIndex) => (
              <Card key={itemIndex}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    工序进度跟踪 - {item.finished_product_name} ({item.finished_product_code})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {item.process_steps && item.process_steps.length > 0 ? (
                    <ProductionStepTracker
                      steps={item.process_steps}
                      orderId={order._id || order.production_order_id}
                      itemIndex={itemIndex}
                      plannedQuantity={item.planned_quantity}
                      readOnly={!['in_progress'].includes(order?.status)}
                      onProgressUpdate={loadOrder}
                    />
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <p className="mb-2">暂无工序步骤</p>
                      {user?.role === 'admin' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRefreshProcessSteps}
                          disabled={actionLoading}
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          刷新工序步骤
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </>
        )}

        {/* Notes */}
        {order?.notes && (
          <Card>
            <CardHeader>
              <CardTitle>备注</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground whitespace-pre-wrap">{order.notes}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Issue Materials Dialog - New AI-Enhanced Component */}
      <MaterialIssueDialog
        open={issueDialogOpen}
        onClose={() => setIssueDialogOpen(false)}
        workOrder={order}
        onSuccess={loadOrder}
      />

      {/* Complete Work Order Dialog */}
      <CompleteWorkOrderDialog
        open={completeDialogOpen}
        onClose={() => setCompleteDialogOpen(false)}
        workOrder={order}
        onSuccess={handleCompleteSuccess}
      />
    </Layout>
  );
}

export default ProductionDetailPage;
